import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {reduxForm} from 'redux-form';
import Header, {headerItemsLabels} from '../../../../Common/Header/header';
import {Prompt} from 'react-router-dom';
import Overlay from 'infrastructure/js/components/Overlay/overlay';
import useLabels from 'infrastructure/js/hooks/useLabels';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox.js';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Validation from 'infrastructure/js/components/controls/controlsValidations';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import RadioButton from 'infrastructure/js/components/controls/RadioButton/radioButton';
import {enumTypes, getEnumLabel, getEnumValue} from '../../../../../utils/enumHelper';
import List from 'infrastructure/js/components/List/list';
import QplTestListItem from '../QplTestListItems/QplTestListItem/qplTestListItem';
import {
  FormModes,
  attachmentTypeOptions,
  getOptionByValue,
  qplTestTypeOptions, frequencyTypesOptions
} from '../../utils/qplHelper';
import {operatorOptions} from '../../../../Common/Controls/AddRemoveList/AddRemoveListItem/RangeListItem/rangeListItem';
import CreateQplTestFieldDialog from '../CreateQplTestFieldDialog/createQplTestFieldDialog';
import {dialogsNames} from '../../../../../enums/dialogsNames';
import CommonDialogWrapper from '../../../../Dialogs/CommonDialogWrapper/commonDialogWrapper';

import './qplTestForm.scss';

const getTestFieldsStructure = (data) => {
  const testFields = Object.values(data?.fields?.reduce((acc, item) => {
      const key = item.setNumber - 1;
      if (!acc[key]) acc[key] = [];
      //Workaround
      if (item.field.type === 'CHOICE_LIST') {
        item.field.showPredefinedValues = (item.value === getEnumValue(enumTypes.CHOICE_LIST_VALUE)('PREDEFINED_VALUE'));
      }
      else if (item.field.type === 'FREQUENCY') {
        item.field.showPerMaterialQuantity = (item.value === getEnumValue(enumTypes.FREQUENCY_VALUE)('PER_MATERIAL_QUANTITY'));
      }
      if (!item.field.defaultFieldOrder) {
        item.field.defaultFieldOrder = item.fieldOrder;
      }
      acc[key].push(item?.field);
      return acc;
    }, {})
  );
  return testFields;
}

const getInitialSetsData = (items=[]) => {
  let resSets = Object.values(items.reduce((acc, item) => {
      const key = item.setNumber - 1;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {})
  );

  let result = [];

  resSets.forEach((resSet, index) => {
    let resObj = {};
    resSet.forEach((item, index) => {

      if (item.field.type === 'ATTACHMENT') {
        resObj[item.field.name] = item?.advancedValues?.map((val, index) => {
          return {
            attachmentType: getOptionByValue(attachmentTypeOptions(), val.mainValue),
            attachmentCount: val.secondValue
          }
        }) ?? [{}];
      }
      else if (item.field.type === 'CHOICE_LIST') {
        resObj.choiceListSelect =  {
          value: getEnumValue(enumTypes.CHOICE_LIST_VALUE)(item.value),
          label: getEnumLabel(enumTypes.CHOICE_LIST_VALUE)(item.value)
        };
        if (item.value === getEnumValue(enumTypes.CHOICE_LIST_VALUE)('PREDEFINED_VALUE')) {
          resObj.choiceList = item?.advancedValues?.map((val, index) => {
            return {
              operator: operatorOptions.find((op) => op.value === val.operator),
              quantity: val.mainValue,
              minQuantity: val.secondValue,
            }
          });
        }
        else {
          resObj.choiceList = [{}];
        }
      }
      else if (item.field.type === 'FREQUENCY') {
        resObj.frequencySelect =  {
          value: getEnumValue(enumTypes.FREQUENCY_VALUE)(item.value),
          label: getEnumLabel(enumTypes.FREQUENCY_VALUE)(item.value)
        };
        if (item.value === getEnumValue(enumTypes.FREQUENCY_VALUE)('PER_MATERIAL_QUANTITY')) {
          resObj.frequency = item?.advancedValues?.map((val, index) => {
            return {
              operator: operatorOptions.find((op) => op.value === val.operator),
              quantity: val.mainValue,
              minQuantity: val.secondValue,
              frequencyInterval: val.thirdValue,
              frequencyType: frequencyTypesOptions()?.find(op => op.value === val.freeText),
            }
          });
        }
        else {
          resObj.frequency = [{}];
        }
      }
      else if (item.field.type === 'PASS_CRITERIA') {
        resObj.passCriteria = {
          value: getEnumValue(enumTypes.PASS_CRITERIA)(item.value),
          label: getEnumLabel(enumTypes.PASS_CRITERIA)(item.value)
        };
      }
      else {
        // const fieldName = item.field.name + '-' + item.id;
        resObj[item.field.name] = (item.field.type === 'BOOLEAN') ? (item.value === 'true') : item.value;
      }
    });

    result.push(resObj);
  })

  return result;
}

const getInitialTestCode = (codeNames = [], code) => {
  return codeNames?.find(item => item.label === code) ?? null;
}

const getAttachmentsSubmitValues = (value) => {
  return value?.filter((item) => {return item.attachmentType?.value && (item.attachmentCount || item.attachmentCount === 0)})
              ?.map((item, index) => {
                  return {
                    mainValue: item.attachmentType?.value,
                    secondValue: item.attachmentCount,
                    internalFieldOrder: index + 1
                  }
    });
}

const getSetOfValuesDefaults = () => {
  return {
    attachments: [{}],
    choiceList: [{}],
    frequency: [{}],
    comments: false,
    passCriteria: { value: getEnumValue(enumTypes.PASS_CRITERIA)('AVERAGE'),
      label: getEnumLabel(enumTypes.PASS_CRITERIA)('AVERAGE')},
    minimumValue: '',
    maximumValue: '',
    minimumUnit: '',
    maximumUnit: '',
    frequencySelect: {value: getEnumValue(enumTypes.FREQUENCY_VALUE)('ONE_RAW_MATERIAL'),
      label: getEnumLabel(enumTypes.FREQUENCY_VALUE)('ONE_RAW_MATERIAL')},
  }
}

const getFieldsSubmitData = (setsOfValuesList=[], testFields=[]) => {
  let result = [];

  setsOfValuesList?.forEach((fieldsObj, listIndex) => {

    for (const [key, value] of Object.entries(fieldsObj)) {
      let field = testFields?.[listIndex].find(item => item.name === key);
      if (field) {
        if (field.type === 'ATTACHMENT') {
          result.push(
            {
              fieldId: field.id,
              value: 'ATTACHMENT',
              fieldOrder: field.defaultFieldOrder,
              setNumber: listIndex + 1,
              advancedValues: getAttachmentsSubmitValues(value),
            });
          continue;
        }
        else if (field.type === 'CHOICE_LIST') {
          const choiceListSelectValue = fieldsObj['choiceListSelect']?.value ?? null;

          result.push(
            {
              fieldId: field.id,
              value: choiceListSelectValue,
              fieldOrder: field.defaultFieldOrder,
              setNumber: listIndex + 1,
              advancedValues: choiceListSelectValue === getEnumValue(enumTypes.CHOICE_LIST_VALUE)('PREDEFINED_VALUE') ?
                value?.map((item, index) => {
                  return {
                    operator: item.operator?.value,
                    mainValue: item.quantity,
                    secondValue: item.minQuantity,
                    internalFieldOrder: index + 1
                  }
                }) : [],
            });
          continue;
        }
        else if (field.type === 'FREQUENCY') {
          const frequencySelectValue = fieldsObj['frequencySelect']?.value ?? null;

          result.push(
            {
              fieldId: field.id,
              value: frequencySelectValue,
              fieldOrder: field.defaultFieldOrder,
              setNumber: listIndex + 1,
              advancedValues: frequencySelectValue === getEnumValue(enumTypes.FREQUENCY_VALUE)('PER_MATERIAL_QUANTITY') ?
                value?.map((item, index) => {
                  return {
                    operator: item.operator?.value,
                    mainValue: item.quantity,
                    secondValue: item.minQuantity,
                    thirdValue: item.frequencyInterval,
                    freeText: item.frequencyType?.value,
                    internalFieldOrder: index + 1
                  }
                }) : [],
            });
          continue;
        }
        else if (field.type === 'PASS_CRITERIA') {
          result.push(
            {
              fieldId: field.id,
              value: value.value,
              fieldOrder: field.defaultFieldOrder,
              setNumber: listIndex + 1,
            });
          continue;
        }

        result.push(
          {
            fieldId: field.id,
            value: value === null ? '' : value,
            // value: value === '' ? null : value,
            fieldOrder: field.defaultFieldOrder,
            setNumber: listIndex + 1,
          });
      }
    }
  })
  return result;
}

const maxLengthDropDown = Validation.dropdown.maxLength(100);

const getOptionalFilds = (testType, formData = {}) => {
  const isQuantity = (testType === getEnumValue(enumTypes.TEST_TYPE)('QUANTITATIVE'));
  return isQuantity ? formData?.quantityOptionalFields : formData?.qualityOptionalFields;
}


function QplTestForm ({ history,
                    match,
                    handleSubmit,
                    pristine,
                    initialize,
                    change,
                    initialData,
                    formData,
                    actions,
                    dialogActions,
                  }) {



  const labels = useLabels('mat.forms.qplTestForm.');
  const formMode = match.params.mode ?? FormModes.CREATE;
  const qplId = match.params.qplId;

  const [confirmedAbandon, setConfirmedAbandon] = useState(false);
  const [testType, setTestType] = useState(formMode === FormModes.EDIT ? initialData?.type : getEnumValue(enumTypes.TEST_TYPE)('QUANTITATIVE'));
  const [testFields, setTestFields] = useState(null);
  const [dummyKey, setDummyKey] = useState(0);
  const nextLocationPath = useRef(null);

  useEffect(() => {
    let initialValues = {
      testTypeBtn: getEnumValue(enumTypes.TEST_TYPE)('QUANTITATIVE'),
      setsOfValuesList: [getSetOfValuesDefaults()]
    };

    let fields = [JSON.parse( JSON.stringify(formData?.get('formData')?.quantityFields) )] ?? [];

    if (formMode === FormModes.EDIT && initialData) {
      initialValues.testName = initialData.name;
      initialValues.testTypeBtn = initialData.type;
      initialValues.testCode = getInitialTestCode(formData?.get('formData')?.codeNameFields, initialData.description);
      initialValues.setsOfValuesList = getInitialSetsData(initialData.fields);

      fields = getTestFieldsStructure(initialData);
    }

    setTestFields(fields);

    initialize(initialValues);

  }, [initialData]);

  useEffect(() => {
    if (confirmedAbandon) {
      if (nextLocationPath.current) {
        history.push(nextLocationPath.current);
      }
      else if (nextLocationPath.current === '') {
        history.goBack();
      }
      nextLocationPath.current = null;
    }
  }, [confirmedAbandon]);

  const getUpperHeaderItems = () => {
    return {
      buttons: [
        {
          id: 'cancel',
          label: formMode === FormModes.CLONE ? headerItemsLabels.CANCEL : headerItemsLabels.BACK,
          icon: formMode === FormModes.CLONE ? '' : 'pl pl-arrow-left',
          className: formMode === FormModes.CLONE ? 'no-icon' : '',
          action: () => history.replace(`/Administration/matSettings/qualityProductLists/qplForm/${qplId}/edit`),
        },
        {
          id: 'save',
          label: headerItemsLabels.SAVE,
          className: 'no-icon',
          action: handleSubmit(onSubmit),
          disabled: pristine || formData?.get('loading')
        },
      ]
    }
  }

  const getHeaderItems = () => {
    return {
      buttons: [
        ...(formMode !== FormModes.CLONE ?
          [{
            id: 'createField',
            icon: 'pl pl-icon-add',
            label: headerItemsLabels.CREATE_FIELD,
            action: () => actions.onCreateNewField(),
          },
            {
              id: 'exportToPdf',
              icon: 'pl pl-export-icon',
              tooltip: headerItemsLabels.EXPORT_TO_PDF,
              action: actions.exportToPdf,
              disabled: !initialData?.id,
              actionData: [{id: initialData?.id}],
            }] : []),
      ],
    };
  };

  const onSubmit = (data) => {
    const submitData = (formMode === FormModes.CLONE) ?
        {
          name: data.testName,
          id: initialData?.id,
        } :
        {
          name: data.testName,
          description: data.testCode?.label,
          type: data.testTypeBtn,
          fields: getFieldsSubmitData(data.setsOfValuesList, testFields)
        }
    if (formMode === FormModes.CREATE) {
      submitData.qplId = qplId;
    }
    if (formMode === FormModes.EDIT) {
      submitData.id = initialData?.id;
    }

    actions.submit(submitData, formMode, onSubmitCallback);
  }

  const onSubmitCallback = (qplTest) => {

    if (formMode === FormModes.CLONE) {
        setConfirmedAbandon(true);
        history.goBack();
        return;
    }
    const nextLocation = {
      pathname: formMode === FormModes.CREATE ? `${match.url}/${qplTest?.id}/edit` : '',
      search: `r=${Date.now()}`,
    }
    nextLocationPath.current = nextLocation;
    setConfirmedAbandon(true);
  }

  const onRowAddCallback = () => {
    const isQuantitative = testType === getEnumValue(enumTypes.TEST_TYPE)('QUANTITATIVE');

    const sourceFields = isQuantitative
      ? formData?.get('formData')?.quantityFields
      : formData?.get('formData')?.qualityFields;

    if (!sourceFields) return;

    //Note: clone the sourceFields - for flat objects only
    const fieldsToAdd = sourceFields.map(item => ({ ...item }));

    setTestFields(prev => [...prev, fieldsToAdd]);
  };

  const onRowRemoveCallback = rowIndex => {
    setTestFields(prev =>
      prev.filter((field, index) => index !== rowIndex)
    );
  };

  const onRowCloneCallback = (rowIndex) => {
    if (!testFields?.[rowIndex]) return;

    setTestFields(prev => {
      const clone = [...prev[rowIndex]];
      return prev.toSpliced(rowIndex + 1, 0, clone);
    });
  }

  const onFieldAddHandler = (item, index) => {

    const newFields = [...testFields];
    const row = newFields[index];
    if (!row) return;

    const newRow = [...row];
    const fieldIndex = newRow.length;

    const fieldToAdd = {
            ...item,
            name: item.type === 'ATTACHMENT' ? item.name  : `${item.name}-${index}-${fieldIndex}`,
            defaultFieldOrder: fieldIndex + 1,
          };

    newRow.push(fieldToAdd);
    newFields[index] = newRow;

    change(`setsOfValuesList[${index}].${fieldToAdd.name}`, item.type === 'ATTACHMENT' ? [{}] : null);

    setTestFields(newFields);
    setDummyKey(prevVal => 1 - prevVal);
  }

  const onFieldRemoveHandler = (itemToRemove, index) => {

    let newFields = [...testFields];
    let row = newFields[index];
    if (row) {
      let foundIndex = row.findIndex(item => item.name === itemToRemove.name);
      if (foundIndex !== -1) {
        let reduxName = `setsOfValuesList[${index}].${itemToRemove.name}`;
        change(reduxName, null);
        row.splice(foundIndex, 1);
        row.forEach((item, index) => {
          item.defaultFieldOrder = index + 1;
        });
        setTestFields(newFields);
        setDummyKey((prevVal) => 1 - prevVal);
      }
    }
  }

  const onFieldChangeHandler = (index, type, value) => {

    let newFields = [...testFields];
    let row = newFields[index];
    if (row) {
      const found = row.find(item => item.type === type);
      if (found) {
        if (type === 'CHOICE_LIST') {
          found.showPredefinedValues = (value === getEnumValue(enumTypes.CHOICE_LIST_VALUE)('PREDEFINED_VALUE'));
        }
        else if (type === 'FREQUENCY') {
          found.showPerMaterialQuantity = value;
        }
        setTestFields(newFields);
        setDummyKey((prevVal) => 1 - prevVal);
      }
    }
  }

  const onTestTypeChange = (value) => {

    const isQuantitative = value === getEnumValue(enumTypes.TEST_TYPE)('QUANTITATIVE');

    const sourceFields = isQuantitative
      ? formData?.get('formData')?.quantityFields
      : formData?.get('formData')?.qualityFields;

    if (!sourceFields) return;

    change('setsOfValuesList', [getSetOfValuesDefaults()]);
    setTestType(value);

    //Note: clone the sourceFields - for flat objects only
    const newFields = sourceFields.map(item => ({ ...item }));
    setTestFields( [newFields]);

    setDummyKey(prevVal => 1 - prevVal);
  }

  const getTestCodeOptions = () => {
    return formData?.get('formData')?.codeNameFields;
  }

  const getTitle = () => {
    switch (formMode) {
      case FormModes.EDIT:
        return  labels.get('title.edit');
      case FormModes.CLONE:
        return  labels.get('title.clone');
      default:
        return  labels.get('title.create');
    }
  }

  const getTitleDetails = () => {
    return (formMode === FormModes.CLONE || formMode === FormModes.EDIT) ? ` - ${initialData?.name ?? ''}` : '';
  }

  const handleAbandon = (nextLocation) => {
    if (!confirmedAbandon) {
      actions.abandonDirtyForm(() => {
        setConfirmedAbandon(true);
        nextLocationPath.current = nextLocation.pathname;
      });
      return false;
    }
    return true;
  };

  const renderLoadingOverlay = () => {
    if (formData.get('loading')) {
      return <Overlay.Loading />;
    }
    return null;
  };

  const listItemRenderer = (data) => {
    const optionalFields = getOptionalFilds(testType, formData?.get('formData'));

    return (
      <QplTestListItem
        {...data}
        testFields={testFields}
        optionalFields={optionalFields ?? []}
        onFieldAdd={onFieldAddHandler}
        onFieldRemove={onFieldRemoveHandler}
        onFieldChange={onFieldChangeHandler}
        change={change}
      />
    )
  }

  const renderSetOfValuesList = () => {
    return (
      <div className="sets-of-values-list-container">
        <List
          updateKey={dummyKey?.toString()}
          id="setsOfValuesList"
          name="setsOfValuesList"
          className='sets-of-values-list'
          itemRenderer={listItemRenderer}
          preSelectedItems={[null]}
          onAddCallback={onRowAddCallback}
          onRemoveCallback={onRowRemoveCallback}
          onCloneCallback={onRowCloneCallback}
          addButtonLabel={labels.get('button.addSetOfValues')}
          defaultItem={getSetOfValuesDefaults()}
          minFieldsCount={1}
          maxItemsToRender={20}
          isCloneEnabled={true}
        />
      </div>
    )
  }

  const renderFormContent = () => {
    if (formMode === FormModes.CLONE) {
      return (
          <InputSection label={labels.get('testName') + '*'} htmlFor="testName">
            <TextField id='testName' name='testName' className='wide-textfield'
                       validate={Validation.required}
            />
          </InputSection>
      )
    }
    return (
        <div className='form-content'>
          <div className='qpl-test-params-section'>
            <InputSection label={labels.get('testName') + '*'} htmlFor="testName">
              <TextField id='testName' name='testName' className='wide-textfield'
                         maxLength={100}
                         validate={Validation.required}
                         disabled={formMode === FormModes.EDIT && initialData?.qpl?.isActive}
              />
            </InputSection>
            <InputSection label={labels.get('testCode') + '*'} htmlFor="testCode" className='wide'>
              <Combobox id='testCode'
                        name='testCode'
                        options={getTestCodeOptions()}
                        validate={[Validation.dropdown.required, maxLengthDropDown]}
                        allowNewOption={true}
              />
            </InputSection>
          </div>

          <InputSection label={labels.get('testType')} htmlFor="testTypeBtn"
                        className="inline left-side">
            <RadioButton id="testTypeBtn" name="testTypeBtn" className="short-textfield"
                         labelsAndValues={qplTestTypeOptions()}
                         onChange={onTestTypeChange}
            />
          </InputSection>

          {testFields && renderSetOfValuesList()}
        </div>
    )
  }

  const reloadOptionalFields = () => {
    actions.fetchOptionalFields();
  }

  return (
    <div className="pl-form qpl-test-form">
      <div className="title">
        <span>{getTitle()}</span>
        <span className="title-details">{getTitleDetails()}</span>
      </div>

      <div className='qpl-test-header-wrapper'>
        <Header className='form-header' {...getUpperHeaderItems()} />
      </div>

      <div className='header-wrapper'>
        <Header className='form-header' {...getHeaderItems()} />
      </div>

      {renderFormContent()}

      <Prompt when={!pristine} message={handleAbandon}/>
      {renderLoadingOverlay()}

      <CommonDialogWrapper dialogComponent={CreateQplTestFieldDialog}
        dialogName={dialogsNames.CREATE_QPL_TEST_FIELD_DIALOG}
        actions={dialogActions}
        reloadParentComponent={reloadOptionalFields}
      />
    </div>
  );
}


QplTestForm.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  actions: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
};

export default reduxForm({
  form: 'qplTestForm',
})(QplTestForm);





